import { HttpContext, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    signal,
    WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { LicenseListBloc } from '@connect-services/client/blocs/license-list.bloc';
import { PeopleBloc } from '@connect-services/client/blocs/people.bloc';
import { AppLogoComponent } from '@connect-services/client/components/app-logo/app-logo.component';
import { BYPASS_INTERCEPTOR } from '@connect-services/client/interceptors/http-errors.interceptor';
import { RouteCompletePathEnum, RoutePathEnum } from '@connect-services/client/models/routes.model';
import { UserDetailFieldInterventionListComponent } from '@connect-services/client/pages/management/organization/user-detail/user-detail-field-intervention-list/user-detail-field-intervention-list.component';
import { UserDetailProjectListComponent } from '@connect-services/client/pages/management/organization/user-detail/user-detail-project-list/user-detail-project-list.component';
import { DetailPanelService, PanelAction } from '@connect-services/client/services/detail-panel.service';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { License, PeopleService, Person, Role, RolesService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { SnackBarService, SnackBarTypeEnum } from '@shared/client/services/snack-bar.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AppLogoComponent,
        RessourcesModule,
        UserDetailProjectListComponent,
        UserDetailFieldInterventionListComponent,
    ],
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
})
export class UserDetailComponent {
    public readonly connectServiceLicense = computed<License | undefined>(() =>
        this.licenses().find((license: License) => license.application === License.ApplicationEnum.CONNECT_SERVICES),
    );
    public readonly licenses: WritableSignal<License[]> = signal([]);
    public readonly person: WritableSignal<Person | null> = signal(null);
    public readonly role: WritableSignal<Role | null> = signal(null);

    protected readonly licenseListBloc = inject(LicenseListBloc);
    protected readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);
    protected readonly peopleBloc = inject(PeopleBloc);
    protected readonly securityService = inject(SecurityService);

    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly detailPanelService = inject(DetailPanelService);
    private readonly peopleService = inject(PeopleService);
    private readonly rolesService = inject(RolesService);
    private readonly router = inject(Router);
    private readonly snackBarService = inject(SnackBarService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.destroyRef.onDestroy(() => {
            this.detailPanelService.clear();
        });
        effect(() => {
            const person = this.peopleBloc.value();
            this.person.set(person);
            if (!person) {
                return;
            }

            this.detailPanelService.actions.set(this.getPanelActions(person));
            this.detailPanelService.navigateTo.set(RouteCompletePathEnum.USERS_ROLES);
            this.detailPanelService.title.set(person.fullName);
            void this.loadExtraData(person);
        });
    }

    private getPanelActions(person: Person): PanelAction[] {
        const canEditUser = this.organizationAccessPermissionsService.canEditUser();
        const canManageUser = this.organizationAccessPermissionsService.canManageUser();

        const actions = [
            new PanelAction({
                defaultDisabledTooltipMessage: this.translocoService.translate('notPossibleEdit'),
                icon: ['fas', 'pen'],
                isActive: canEditUser,
                label: this.translocoService.translate('edit'),
                onClick: this.openUserEdition.bind(this),
                ...(!canEditUser && { disabledReason: [this.translocoService.translate('insufficientPermissions')] }),
            }),
        ];

        if (!person?.isFinalized) {
            return [
                ...actions,
                new PanelAction({
                    icon: ['fas', 'paper-plane'],
                    isActive: canManageUser,
                    label: this.translocoService.translate('resendInvitation'),
                    onClick: this.resendInvitationMail.bind(this, person),
                    type: ButtonType.SECONDARY,
                    ...(!canManageUser && {
                        disabledReason: [this.translocoService.translate('insufficientPermissions')],
                    }),
                }),
            ];
        }

        return [
            ...actions,
            new PanelAction({
                icon: ['fas', 'arrow-rotate-left'],
                isActive: canManageUser,
                label: this.translocoService.translate('resetPassword'),
                onClick: this.resetPassword.bind(this, person),
                type: ButtonType.SECONDARY,
                ...(!canManageUser && { disabledReason: [this.translocoService.translate('insufficientPermissions')] }),
            }),
        ];
    }

    private async loadExtraData(person: Person): Promise<void> {
        if (!person.role) {
            this.role.set(null);
        } else {
            try {
                this.role.set(await firstValueFrom(this.rolesService.roleControllerGetOne({ id: person.role })));
            } catch {
                this.snackBarService.openErrorSnackBar('errorGET');
                this.role.set(null);
            }
        }

        await this.licenseListBloc.load({ assignedOnly: true, person: person.id }, { refresh: true });

        this.licenses.set(this.licenseListBloc.value()!.items);
    }

    private async openUserEdition(): Promise<void> {
        const panelParam = this.person()!.id;

        await this.router.navigate([
            ...RouteCompletePathEnum.USERS_ROLES,
            { outlets: { detailPanel: ['user', panelParam, RoutePathEnum.USER_EDITION] } },
        ]);
    }

    private async resendInvitationMail(person: Person): Promise<void> {
        try {
            await firstValueFrom(
                this.peopleService.personControllerSendInvitation(
                    {
                        id: person.id,
                        updateInvitationTokenDto: {
                            finalizeInvitationPath: `/${RoutePathEnum.FINALIZE_INVITATION}`,
                        },
                    },
                    'response',
                    true,
                    {
                        context: new HttpContext().set(BYPASS_INTERCEPTOR, ['httpErrorsInterceptor']),
                    },
                ),
            );

            this.snackBarService.openSuccessSnackBar('mailSendSuccessfully');
            void this.router.navigate(RouteCompletePathEnum.USERS_ROLES);
        } catch (error: unknown) {
            if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.TooManyRequests) {
                this.snackBarService.openPlainTextSnackBar(
                    error.error?.message ?? error.message,
                    SnackBarTypeEnum.WARNING,
                );

                return;
            }
            this.snackBarService.openErrorSnackBar('errorDefault');
        }
    }

    private async resetPassword(person: Person): Promise<void> {
        try {
            await firstValueFrom(
                this.peopleService.personControllerResetPassword({
                    id: person.id,
                    resetPasswordDto: { changePasswordPath: `/${RoutePathEnum.CHANGE_PASSWORD}` },
                }),
            );
            this.snackBarService.openSuccessSnackBar('resetPasswordDone');
            void this.router.navigate(RouteCompletePathEnum.USERS_ROLES);
        } catch (error: unknown) {
            if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.TooManyRequests) {
                this.snackBarService.openPlainTextSnackBar(
                    error.error?.message ?? error.message,
                    SnackBarTypeEnum.WARNING,
                );

                return;
            }
            this.snackBarService.openErrorSnackBar('errorDefault');
        }
    }
}
